import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import placeholderImage from '../images/Placeholder.png';

const brand = 'rgb(27, 52, 74)';

function StarPicker({ value, onChange }) {
    const [hovered, setHovered] = useState(0);
    return (
        <div style={{ display: 'flex', gap: 6, cursor: 'pointer' }}>
            {[1, 2, 3, 4, 5].map(n => {
                const filled = n <= (hovered || value);
                return (
                    <i
                        key={n}
                        className={`bi ${filled ? 'bi-star-fill' : 'bi-star'}`}
                        onMouseEnter={() => setHovered(n)}
                        onMouseLeave={() => setHovered(0)}
                        onClick={() => onChange(n === value ? 0 : n)}
                        style={{
                            fontSize: '1.6rem',
                            color: filled ? '#e6a817' : 'rgba(27,52,74,0.25)',
                            transition: 'color 0.1s',
                            userSelect: 'none',
                        }}
                    />
                );
            })}
        </div>
    );
}

const RATING_LABELS = { 1: 'Poor', 2: 'Fair', 3: 'Good', 4: 'Great', 5: 'Excellent' };

export default function ReviewOrder() {
    const { orderId } = useParams();

    // page states: loading | ready | already_rated | not_found | error | submitted
    const [pageState,  setPageState]  = useState('loading');
    const [items,      setItems]      = useState([]);
    const [ratings,    setRatings]    = useState({}); // { productId: { rating, comment } }
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        axios.get(`${API_BASE_URL}/api/review/${orderId}`)
            .then(res => {
                const data = res.data.data;
                if (data.rated) { setPageState('already_rated'); return; }
                setItems(data.items);
                setPageState('ready');
            })
            .catch(err => {
                if (err.response?.status === 404) setPageState('not_found');
                else setPageState('error');
            });
    }, [orderId]);

    const setRating = (productId, rating) =>
        setRatings(prev => ({
            ...prev,
            [productId]: { rating, comment: prev[productId]?.comment || '' },
        }));

    const setComment = (productId, comment) =>
        setRatings(prev => ({
            ...prev,
            [productId]: { ...prev[productId], comment },
        }));

    const hasAtLeastOne = Object.values(ratings).some(r => r.rating > 0);

    const handleSubmit = async () => {
        setSubmitting(true);
        setSubmitError('');
        try {
            const payload = Object.entries(ratings)
                .filter(([, v]) => v.rating > 0)
                .map(([productId, v]) => ({ productId, rating: v.rating, comment: v.comment || '' }));

            await axios.post(`${API_BASE_URL}/api/review/${orderId}`, { ratings: payload });
            setPageState('submitted');
        } catch (err) {
            setSubmitError(err.response?.data?.message || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const card = {
        background: '#fff',
        border: '1px solid rgba(27,52,74,0.1)',
        borderRadius: 14,
        padding: '1.5rem',
        boxShadow: '0 2px 12px rgba(27,52,74,0.07)',
        maxWidth: 640,
        margin: '0 auto',
    };

    // ── States ──────────────────────────────────────────────────
    if (pageState === 'loading') {
        return (
            <div style={{ padding: '4rem 1rem', textAlign: 'center', color: 'rgba(27,52,74,0.5)' }}>
                Loading your order…
            </div>
        );
    }

    if (pageState === 'not_found') {
        return (
            <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
                <div style={{ ...card, display: 'inline-block', padding: '2.5rem 2rem' }}>
                    <i className="bi bi-bag-x" style={{ fontSize: '2.5rem', color: 'rgba(27,52,74,0.3)', display: 'block', marginBottom: '1rem' }} />
                    <h2 style={{ color: brand, margin: '0 0 0.5rem' }}>Order Not Found</h2>
                    <p style={{ color: 'rgba(27,52,74,0.55)', margin: '0 0 1.5rem' }}>
                        We couldn't find this order. The link may be incorrect or expired.
                    </p>
                    <Link to="/" style={{ color: brand, fontWeight: 600, fontSize: '0.9rem' }}>Return Home</Link>
                </div>
            </div>
        );
    }

    if (pageState === 'already_rated') {
        return (
            <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
                <div style={{ ...card, display: 'inline-block', padding: '2.5rem 2rem' }}>
                    <i className="bi bi-patch-check-fill" style={{ fontSize: '2.5rem', color: '#e6a817', display: 'block', marginBottom: '1rem' }} />
                    <h2 style={{ color: brand, margin: '0 0 0.5rem' }}>Already Reviewed</h2>
                    <p style={{ color: 'rgba(27,52,74,0.55)', margin: '0 0 1.5rem' }}>
                        This order has already been reviewed. Thank you for your feedback! In the event you would like to do any modifications to your review, please contact us at <a href="mailto:info@castillobc.shop">info@castillobc.shop</a> and we'd be happy to assist you.
                    </p>
                    <Link to="/" style={{ backgroundColor: brand, textDecoration: 'none', color: 'white', fontWeight: 'bold', padding: '0.5rem 1rem', borderRadius: '10px', fontSize: '0.9rem' }}>Return Home</Link>
                </div>
            </div>
        );
    }

    if (pageState === 'submitted') {
        return (
            <div style={{ padding: '4rem 1rem', textAlign: 'center' }}>
                <div style={{ ...card, display: 'inline-block', padding: '2.5rem 2rem' }}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: '2.5rem', color: 'rgb(34,139,34)', display: 'block', marginBottom: '1rem' }} />
                    <h2 style={{ color: brand, margin: '0 0 0.5rem' }}>Thank You!</h2>
                    <p style={{ color: 'rgba(27,52,74,0.55)', margin: '0 0 1.5rem' }}>
                        Your reviews have been submitted. We appreciate your feedback!
                    </p>
                    <Link to="/" style={{  backgroundColor: brand, textDecoration: 'none', color: 'white', fontWeight: 'bold', padding: '0.5rem 1rem', borderRadius: '10px', fontSize: '0.9rem' }}>Continue Shopping</Link>
                </div>
            </div>
        );
    }

    if (pageState === 'error') {
        return (
            <div style={{ padding: '4rem 1rem', textAlign: 'center', color: 'rgba(27,52,74,0.5)' }}>
                Something went wrong loading this page. Please try again later.
            </div>
        );
    }

    // ── Ready ────────────────────────────────────────────────────
    return (
        <div style={{ padding: '2.5rem 1rem 4rem', fontFamily: 'inherit' }}>
            <div style={{ maxWidth: 640, margin: '0 auto' }}>

                {/* Header */}
                <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                    <h1 style={{ color: brand, fontSize: '1.6rem', fontWeight: 700, margin: '0 0 0.4rem' }}>
                        Rate Your Purchase
                    </h1>
                    <p style={{ color: 'rgba(27,52,74,0.55)', fontSize: '0.9rem', margin: 0 }}>
                        Share your experience with your recent order <strong style={{ color: brand }}>{orderId}</strong>.
                        Rating each item is optional — submit when you're ready.
                    </p>
                </div>

                {/* Product rows */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.75rem' }}>
                    {items.map(item => {
                        const r = ratings[item.productId];
                        const rated = r?.rating > 0;
                        return (
                            <div key={item.productId} style={{
                                ...card,
                                padding: '1.25rem',
                                borderLeft: rated ? `4px solid #e6a817` : `4px solid rgba(27,52,74,0.1)`,
                                transition: 'border-color 0.2s',
                            }}>
                                {/* Product info */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '1rem' }}>
                                    <img
                                        src={item.imageUrl || placeholderImage}
                                        alt={item.name}
                                        style={{ width: 60, height: 60, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(27,52,74,0.1)' }}
                                        onError={e => { e.target.src = placeholderImage; }}
                                    />
                                    <div>
                                        <div style={{ fontWeight: 700, color: brand, fontSize: '0.95rem' }}>{item.name}</div>
                                        {item.quantity > 1 && (
                                            <div style={{ fontSize: '0.78rem', color: 'rgba(27,52,74,0.5)', marginTop: 2 }}>
                                                Qty: {item.quantity}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Stars */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '0.85rem' }}>
                                    <StarPicker
                                        value={r?.rating || 0}
                                        onChange={val => setRating(item.productId, val)}
                                    />
                                    {rated && (
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#e6a817' }}>
                                            {RATING_LABELS[r.rating]}
                                        </span>
                                    )}
                                </div>

                                {/* Comment box */}
                                <textarea
                                    disabled={!rated}
                                    value={r?.comment || ''}
                                    onChange={e => setComment(item.productId, e.target.value)}
                                    placeholder={rated ? 'Leave a comment (optional)…' : 'Select a star rating to leave a comment.'}
                                    rows={3}
                                    style={{
                                        width: '100%',
                                        padding: '0.6rem 0.75rem',
                                        border: `1px solid ${rated ? 'rgba(27,52,74,0.25)' : 'rgba(27,52,74,0.1)'}`,
                                        borderRadius: 8,
                                        fontSize: '0.85rem',
                                        color: rated ? brand : 'rgba(27,52,74,0.35)',
                                        background: rated ? '#fff' : 'rgba(27,52,74,0.03)',
                                        resize: 'vertical',
                                        cursor: rated ? 'text' : 'not-allowed',
                                        boxSizing: 'border-box',
                                        outline: 'none',
                                        transition: 'border-color 0.15s, background 0.15s',
                                        fontFamily: 'inherit',
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>

                {/* Submit */}
                {submitError && (
                    <div style={{ marginBottom: '0.75rem', color: 'rgb(180,30,40)', fontSize: '0.85rem', textAlign: 'center' }}>
                        <i className="bi bi-exclamation-circle" style={{ marginRight: 5 }} />
                        {submitError}
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={!hasAtLeastOne || submitting}
                    style={{
                        display: 'block',
                        width: '100%',
                        padding: '0.85rem',
                        background: hasAtLeastOne && !submitting ? brand : 'rgba(27,52,74,0.15)',
                        color: hasAtLeastOne && !submitting ? '#fff' : 'rgba(27,52,74,0.4)',
                        border: 'none',
                        borderRadius: 10,
                        fontSize: '1rem',
                        fontWeight: 700,
                        cursor: hasAtLeastOne && !submitting ? 'pointer' : 'not-allowed',
                        transition: 'background 0.2s, color 0.2s',
                        letterSpacing: '0.01em',
                    }}
                >
                    {submitting ? 'Submitting…' : hasAtLeastOne ? 'Submit Reviews' : 'Rate at least one item to submit'}
                </button>

                <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.78rem', color: 'rgba(27,52,74,0.4)' }}>
                    Only items you've rated will be submitted. This link can only be used once.
                </p>
            </div>
        </div>
    );
}
